import { format, parse } from "date-fns";
import { ja } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useMemo } from "react";

import { cn } from "@wellcheck/components/lib/cn";
import { buttonVariants } from "@wellcheck/components/ui/button";
import { Calendar } from "@wellcheck/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@wellcheck/components/ui/popover";

import type { DateRange } from "react-day-picker";

export interface PeriodRangePickerProps {
  /** yyyy-MM-dd 形式の開始日 (未選択時は空文字) */
  startIso: string;
  /** yyyy-MM-dd 形式の終了日 (未選択時は空文字) */
  endIso: string;
  /** 値変更時に呼ばれる。開始日のみ選択中は endIso が空のまま渡る */
  onChange: (value: { startIso: string; endIso: string }) => void;
  /** バリデーションエラー有無 (赤枠表示用) */
  invalid?: boolean;
  /** 開始日として選択不可な範囲。今日より前を弾く想定 */
  disablePast?: boolean;
  /** 開始日の下限 (これより前を選択不可にする)。未指定なら今日 */
  minStart?: Date;
}

/**
 * PeriodRangePicker — 開始日と終了日を 1 つのカレンダーで選ぶ Range Picker。
 *
 * 「開始日から最大 1 ヶ月」の制約を可視化するため、開始日を選んだ後は
 * `disabled={{ before: start, after: addDays(start, 30) }}` で範囲外を選択不可にする。
 * 終了日まで決まったら Popover を閉じる。
 */
const ISO_FMT = "yyyy-MM-dd";

function parseIso(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = parse(iso, ISO_FMT, new Date(0));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toIso(date: Date | undefined): string {
  return date ? format(date, ISO_FMT) : "";
}

export function PeriodRangePicker({
  startIso,
  endIso,
  onChange,
  invalid,
  disablePast = true,
  minStart,
}: PeriodRangePickerProps) {
  const selected = useMemo<DateRange | undefined>(() => {
    const from = parseIso(startIso);
    const to = parseIso(endIso);
    if (!from && !to) return undefined;
    return { from, to };
  }, [startIso, endIso]);

  const handleSelect = (range: DateRange | undefined) => {
    onChange({
      startIso: toIso(range?.from),
      endIso: toIso(range?.to),
    });
  };

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // 開始日が決まったら「開始日から最大 30 日 (= 1 ヶ月)」を選択範囲に限定する。
  // 開始日未選択時は disablePast のみ適用。
  const disabled = useMemo(() => {
    if (selected?.from) {
      const max = new Date(selected.from);
      max.setDate(max.getDate() + 30);
      return [{ before: selected.from }, { after: max }];
    }
    return disablePast ? [{ before: minStart ?? today }] : undefined;
  }, [selected?.from, today, disablePast, minStart]);

  const label = formatRangeLabel(selected);

  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-invalid={invalid || undefined}
        className={cn(
          buttonVariants({ variant: "outline" }),
          "w-full justify-start text-left font-normal",
          !selected?.from && "text-muted-foreground",
        )}
      >
        <CalendarIcon className="size-4" />
        {label}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={selected}
          onSelect={handleSelect}
          disabled={disabled}
          defaultMonth={selected?.from ?? minStart ?? today}
          locale={ja}
        />
      </PopoverContent>
    </Popover>
  );
}

function formatRangeLabel(range: DateRange | undefined): string {
  if (!range?.from) return "期間を選択してください";
  const from = format(range.from, "yyyy/MM/dd", { locale: ja });
  if (!range.to) return `${from} 〜 (終了日を選択)`;
  const to = format(range.to, "yyyy/MM/dd", { locale: ja });
  return `${from} 〜 ${to}`;
}
